import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { useFinalizeProgressUpdate, useDeleteProgressUpdate } from "@/hooks/use-progress-updates";
import type { ProgressUpdateSummary, ProgressUpdate } from "@/types/entities";

interface ProgressUpdateCardProps {
  progressUpdate: ProgressUpdateSummary | ProgressUpdate;
  krId: string;
  isAuthor: boolean;
  canSeeContent: boolean;
}

function ContentDisplay({
  progressUpdate,
  canSeeContent,
}: {
  progressUpdate: ProgressUpdateSummary | ProgressUpdate;
  canSeeContent: boolean;
}) {
  const hasContent = "content" in progressUpdate && progressUpdate.content != null;

  if (hasContent) {
    return (
      <p className="whitespace-pre-wrap text-sm">
        {(progressUpdate as ProgressUpdate).content}
      </p>
    );
  }

  if (canSeeContent) {
    return (
      <p className="text-sm text-[hsl(var(--muted-foreground))] italic">No content</p>
    );
  }

  return (
    <p className="text-sm text-[hsl(var(--muted-foreground))] italic">
      Content restricted
    </p>
  );
}

function FinalizeDialog({
  open,
  onOpenChange,
  krId,
  progressUpdateId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  krId: string;
  progressUpdateId: string;
}) {
  const finalize = useFinalizeProgressUpdate(krId, progressUpdateId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Finalize Progress Update</DialogTitle>
          <DialogDescription>
            Once finalized, this progress update cannot be edited or reverted to draft.
            Your supervisor will be notified.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              finalize.mutate(undefined, {
                onSuccess: () => onOpenChange(false),
              });
            }}
            disabled={finalize.isPending}
          >
            {finalize.isPending ? "Finalizing..." : "Finalize"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({
  open,
  onOpenChange,
  krId,
  progressUpdateId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  krId: string;
  progressUpdateId: string;
}) {
  const deleteUpdate = useDeleteProgressUpdate(krId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Progress Update</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this draft progress update? This action
            cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              deleteUpdate.mutate(progressUpdateId, {
                onSuccess: () => onOpenChange(false),
              });
            }}
            disabled={deleteUpdate.isPending}
          >
            {deleteUpdate.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ProgressUpdateCard({
  progressUpdate,
  krId,
  isAuthor,
  canSeeContent,
}: ProgressUpdateCardProps) {
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const isDraft = progressUpdate.status === "draft";

  return (
    <>
      <Card>
        <CardContent className="py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant={isDraft ? "secondary" : "accent"}>
                  {isDraft ? "Draft" : "Finalized"}
                </Badge>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">
                  {new Date(progressUpdate.created_at).toLocaleDateString()}
                </span>
                {progressUpdate.finalized_at && (
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">
                    Finalized{" "}
                    {new Date(progressUpdate.finalized_at).toLocaleDateString()}
                  </span>
                )}
              </div>

              <ContentDisplay
                progressUpdate={progressUpdate}
                canSeeContent={canSeeContent}
              />
            </div>

            {isAuthor && isDraft && (
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFinalizeDialogOpen(true)}
                >
                  Finalize
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <FinalizeDialog
        open={finalizeDialogOpen}
        onOpenChange={setFinalizeDialogOpen}
        krId={krId}
        progressUpdateId={progressUpdate.id}
      />
      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        krId={krId}
        progressUpdateId={progressUpdate.id}
      />
    </>
  );
}
